import { useEffect, useId, useRef, useState } from 'react';

/** Tick rate for the live clock that drives the scrolling X-axis (ms) */
const LIVE_TICK_MS = 100;

/** Window of data shown in the graph (ms) */
const WINDOW_MS = 10_000;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Interpolate between two hex/rgb colors by t ∈ [0, 1] */
function lerpColor(
  a: [number, number, number],
  b: [number, number, number],
  t: number,
): string {
  const r = Math.round(a[0] + (b[0] - a[0]) * t);
  const g = Math.round(a[1] + (b[1] - a[1]) * t);
  const bl = Math.round(a[2] + (b[2] - a[2]) * t);
  return `rgb(${r},${g},${bl})`;
}

const RED: [number, number, number] = [220, 50, 50];
const YELLOW: [number, number, number] = [220, 180, 40];
const GREEN: [number, number, number] = [60, 200, 80];

/**
 * Map an FPS value to a color: red (0) → yellow (half) → green (max).
 */
function fpsColor(v: number, maxFps: number): string {
  const t = Math.max(0, Math.min(1, v / maxFps));
  if (t < 0.5) return lerpColor(RED, YELLOW, t * 2);
  return lerpColor(YELLOW, GREEN, (t - 0.5) * 2);
}

// ---------------------------------------------------------------------------
// FPS Sparkline
// ---------------------------------------------------------------------------

export interface FpsSparklinePoint {
  t: number;
  v: number;
}

interface FpsSparklineGraphProps {
  data: FpsSparklinePoint[];
  width?: number;
  height?: number;
  /** FPS that maps to full green (default 120) */
  maxFps?: number;
}

/**
 * Live-scrolling FPS sparkline.
 * The line is drawn as individual colored segments so each segment
 * can be tinted based on its FPS value (red → yellow → green).
 */
export function FpsSparklineGraph({
  data,
  width = 160,
  height = 44,
  maxFps = 120,
}: FpsSparklineGraphProps) {
  const uid = useId();
  const clipId = `fps-clip-${uid.replace(/:/g, '')}`;

  // Re-render every LIVE_TICK_MS so the graph scrolls even between new data points
  const [now, setNow] = useState(() => Date.now());
  const rafRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let running = true;
    const tick = () => {
      if (!running) return;
      setNow(Date.now());
      rafRef.current = setTimeout(tick, LIVE_TICK_MS);
    };
    rafRef.current = setTimeout(tick, LIVE_TICK_MS);
    return () => {
      running = false;
      if (rafRef.current) clearTimeout(rafRef.current);
    };
  }, []);

  const pad = 2;
  const plotW = width - pad * 2;
  const plotH = height - pad * 2;
  const windowStart = now - WINDOW_MS;

  const toX = (t: number) => pad + ((t - windowStart) / WINDOW_MS) * plotW;
  const toY = (v: number) =>
    pad + plotH - (Math.max(0, Math.min(maxFps, v)) / maxFps) * plotH;

  // Filter to visible data only (slightly outside left edge to avoid clipping)
  const visible = data.filter((d) => d.t >= windowStart - 200);

  const labelStyle: React.CSSProperties = {
    fontSize: '9px',
    fill: 'rgba(255,255,255,0.45)',
    fontFamily: 'inherit',
  };

  // Build colored segments: each consecutive pair of points is one <line>
  const segments: React.ReactNode[] = [];
  if (visible.length >= 2) {
    for (let i = 0; i < visible.length - 1; i++) {
      const a = visible[i];
      const b = visible[i + 1];
      const midV = (a.v + b.v) / 2;
      const color = fpsColor(midV, maxFps);
      segments.push(
        <line
          key={i}
          x1={toX(a.t)}
          y1={toY(a.v)}
          x2={toX(b.t)}
          y2={toY(b.v)}
          stroke={color}
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />,
      );
    }
  }

  // Filled area under the line using a gradient matching the line colors
  // We build a polygon from the points + baseline
  let areaPath = '';
  if (visible.length >= 2) {
    const pts = visible.map(
      (d) => `${toX(d.t).toFixed(1)},${toY(d.v).toFixed(1)}`,
    );
    const firstX = toX(visible[0].t).toFixed(1);
    const lastX = toX(visible[visible.length - 1].t).toFixed(1);
    const baseY = (pad + plotH).toFixed(1);
    areaPath = `M ${pts.join(' L ')} L ${lastX},${baseY} L ${firstX},${baseY} Z`;
  }

  const last = visible.length > 0 ? visible[visible.length - 1] : null;
  const lastColor = last ? fpsColor(last.v, maxFps) : GREEN.join(',');

  return (
    <svg
      width={width}
      height={height + 12}
      style={{ display: 'block', overflow: 'visible' }}
    >
      {/* Dark background */}
      <rect
        x={0}
        y={0}
        width={width}
        height={height}
        rx={3}
        fill="rgba(0,0,0,0.55)"
      />

      {/* Horizontal grid lines */}
      {[0.25, 0.5, 0.75].map((frac) => (
        <line
          key={frac}
          x1={pad}
          y1={pad + plotH * (1 - frac)}
          x2={pad + plotW}
          y2={pad + plotH * (1 - frac)}
          stroke="rgba(255,255,255,0.06)"
          strokeWidth={1}
        />
      ))}

      {/* Clipping rect so the scrolling content stays inside the background */}
      <defs>
        <clipPath id={clipId}>
          <rect x={0} y={0} width={width} height={height} rx={3} />
        </clipPath>
      </defs>

      <g clipPath={`url(#${clipId})`}>
        {/* Filled area */}
        {areaPath && <path d={areaPath} fill="rgba(255,255,255,0.06)" />}
        {/* Colored line segments */}
        {segments}
        {/* Current-value dot */}
        {last && (
          <circle cx={toX(last.t)} cy={toY(last.v)} r={2.5} fill={lastColor} />
        )}
      </g>

      {/* Labels */}
      <text x={pad} y={height + 10} style={labelStyle}>
        10s ago
      </text>
      {last && (
        <text
          x={width - pad}
          y={height + 10}
          textAnchor="end"
          style={{ ...labelStyle, fill: lastColor }}
        >
          {last.v} fps
        </text>
      )}
      {!last && (
        <text
          x={width / 2}
          y={height / 2 + 4}
          textAnchor="middle"
          style={{ ...labelStyle, fontSize: '9px' }}
        >
          collecting…
        </text>
      )}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Worker Sparkline
// ---------------------------------------------------------------------------

export interface WorkerSparklinePoint {
  t: number;
  active: number;
  total: number;
}

interface WorkerSparklineGraphProps {
  data: WorkerSparklinePoint[];
  width?: number;
  height?: number;
  /**
   * Model color as rgba (0–1 range) — used to tint the active-worker line.
   * Falls back to a default blue if null.
   */
  modelColor?: [number, number, number, number] | null;
}

/**
 * Live-scrolling worker activity graph.
 * Shows total workers as a dim dashed line and active workers as a
 * solid colored line (tinted with the model color if available).
 */
export function WorkerSparklineGraph({
  data,
  width = 160,
  height = 44,
  modelColor,
}: WorkerSparklineGraphProps) {
  const uid = useId();
  const clipId = `worker-clip-${uid.replace(/:/g, '')}`;

  const [now, setNow] = useState(() => Date.now());
  const rafRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let running = true;
    const tick = () => {
      if (!running) return;
      setNow(Date.now());
      rafRef.current = setTimeout(tick, LIVE_TICK_MS);
    };
    rafRef.current = setTimeout(tick, LIVE_TICK_MS);
    return () => {
      running = false;
      if (rafRef.current) clearTimeout(rafRef.current);
    };
  }, []);

  // Derive a CSS color string from the model color (rgba 0-1) or a default
  const accentColor = modelColor
    ? `rgba(${Math.round(modelColor[0] * 255)},${Math.round(modelColor[1] * 255)},${Math.round(modelColor[2] * 255)},0.9)`
    : 'rgba(100,160,255,0.9)';
  const accentFill = modelColor
    ? `rgba(${Math.round(modelColor[0] * 255)},${Math.round(modelColor[1] * 255)},${Math.round(modelColor[2] * 255)},0.18)`
    : 'rgba(100,160,255,0.18)';

  const pad = 2;
  const plotW = width - pad * 2;
  const plotH = height - pad * 2;
  const windowStart = now - WINDOW_MS;

  const toX = (t: number) => pad + ((t - windowStart) / WINDOW_MS) * plotW;

  const maxTotal = Math.max(...data.map((d) => d.total), 1);
  const toY = (v: number) => pad + plotH - (v / maxTotal) * plotH;

  const visible = data.filter((d) => d.t >= windowStart - 200);

  const labelStyle: React.CSSProperties = {
    fontSize: '9px',
    fill: 'rgba(255,255,255,0.45)',
    fontFamily: 'inherit',
  };

  // Build path strings
  let totalLinePath = '';
  let totalAreaPath = '';
  let activeLinePath = '';
  let activeAreaPath = '';

  if (visible.length >= 2) {
    const totalPts = visible.map(
      (d) => `${toX(d.t).toFixed(1)},${toY(d.total).toFixed(1)}`,
    );
    const activePts = visible.map(
      (d) => `${toX(d.t).toFixed(1)},${toY(d.active).toFixed(1)}`,
    );
    const firstX = toX(visible[0].t).toFixed(1);
    const lastX = toX(visible[visible.length - 1].t).toFixed(1);
    const baseY = (pad + plotH).toFixed(1);

    totalLinePath = `M ${totalPts.join(' L ')}`;
    totalAreaPath = `${totalLinePath} L ${lastX},${baseY} L ${firstX},${baseY} Z`;
    activeLinePath = `M ${activePts.join(' L ')}`;
    activeAreaPath = `${activeLinePath} L ${lastX},${baseY} L ${firstX},${baseY} Z`;
  }

  const last = visible.length > 0 ? visible[visible.length - 1] : null;

  return (
    <svg
      width={width}
      height={height + 12}
      style={{ display: 'block', overflow: 'visible' }}
    >
      {/* Dark background */}
      <rect
        x={0}
        y={0}
        width={width}
        height={height}
        rx={3}
        fill="rgba(0,0,0,0.55)"
      />

      {/* Horizontal grid lines */}
      {maxTotal > 0 &&
        Array.from({ length: maxTotal }, (_, i) => i + 1).map((lvl) => (
          <line
            key={lvl}
            x1={pad}
            y1={toY(lvl)}
            x2={pad + plotW}
            y2={toY(lvl)}
            stroke="rgba(255,255,255,0.05)"
            strokeWidth={1}
          />
        ))}

      <defs>
        <clipPath id={clipId}>
          <rect x={0} y={0} width={width} height={height} rx={3} />
        </clipPath>
      </defs>

      <g clipPath={`url(#${clipId})`}>
        {visible.length >= 2 ? (
          <>
            {/* Total workers — dim filled area + dashed line */}
            <path d={totalAreaPath} fill="rgba(255,255,255,0.04)" />
            <path
              d={totalLinePath}
              fill="none"
              stroke="rgba(255,255,255,0.2)"
              strokeWidth={1}
              strokeDasharray="3 2"
            />

            {/* Active workers — colored area + solid line */}
            <path d={activeAreaPath} fill={accentFill} />
            <path
              d={activeLinePath}
              fill="none"
              stroke={accentColor}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />

            {/* Current value dot */}
            {last && (
              <circle
                cx={toX(last.t)}
                cy={toY(last.active)}
                r={2.5}
                fill={accentColor}
              />
            )}
          </>
        ) : (
          <text
            x={width / 2}
            y={height / 2 + 4}
            textAnchor="middle"
            style={{ ...labelStyle, fontSize: '9px' }}
          >
            collecting…
          </text>
        )}
      </g>

      {/* Labels */}
      <text x={pad} y={height + 10} style={labelStyle}>
        10s ago
      </text>
      {last && (
        <text
          x={width - pad}
          y={height + 10}
          textAnchor="end"
          style={{ ...labelStyle, fill: accentColor }}
        >
          {last.active}/{last.total} active
        </text>
      )}
    </svg>
  );
}
